import { mdiHelp } from '@mdi/js'
import Icon from '@mdi/react'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import ConfirmDialog from 'components/ConfirmDialog'
import Logo from 'components/PPLogo'
import PPSelect, { PPSelectOptions } from 'components/PPSelect'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { firstValueFrom } from 'rxjs'
import { resetComposer } from 'services/compose'
import {
  activeDraftSelector,
  composerBulkFileSelector,
  composerIsEmptySelector,
  composerPostEditSelector,
  composerSelectedProfilesCountSelector,
  multiPostsCountSelector
} from 'services/compose/selectors'
import { bulkDeletePosts } from 'services/post/actions'
import { message } from 'services/snackbar'
import { toggleCallout } from 'services/ui/actions'
import { calloutsSelector } from 'services/ui/selectors'
import { StoreThunkDispatch } from 'store/state'
import styles from './Header.pcss'

interface HeaderProps {
  activeView: 'default' | 'bulk'
  onActiveViewChange: (value: 'default' | 'bulk') => void
  onClose: (reset?: boolean) => void
}

export function Header(props: HeaderProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const intl = useIntl()
  const location = useLocation()
  const navigate = useNavigate()
  const selectedProfilesCount = useSelector(composerSelectedProfilesCountSelector)
  const draft = useSelector(activeDraftSelector)
  const postEdit = useSelector(composerPostEditSelector)
  const isEditingPost = Boolean(postEdit)
  const isComposerEmpty = useSelector(composerIsEmptySelector)
  const multiPostsCount = useSelector(multiPostsCountSelector)
  const bulkFile = useSelector(composerBulkFileSelector)
  const calloutIsHidden = useSelector(calloutsSelector).find(c => c.slug === 'composer')?.hidden
  const [isDeleting, setIsDeleting] = useState(false)

  const isDefaultView = props.activeView === 'default'

  const onClear = () => {
    dispatch(resetComposer())
  }

  const showInfobox = () => {
    dispatch(toggleCallout('composer'))
  }

  const close = () => {
    // reset composer on close if editing post or draft
    const shouldReset = Boolean(draft) || isEditingPost
    props.onClose(shouldReset)
  }

  const viewOptions: PPSelectOptions = {
    default: { label: <FormattedMessage id="composer.header-labels.default-view" /> },
    bulk: { label: <FormattedMessage id="composer.header-labels.bulk-view" /> }
  }

  const onDelete = () => {
    if (postEdit) {
      setIsDeleting(true)
      firstValueFrom(dispatch(bulkDeletePosts([postEdit.id], [postEdit.ppid], postEdit.type, false, postEdit.bucketId)))
        .then(() => {
          const locationState = location.state as any || {}
          const opener = locationState.opener
          const backUrl = opener && opener !== '/composer' ? opener : '/posts'
          navigate(backUrl, {
            state: {
              refresh: Date.now().toString(),
              search: locationState.search
            }
          })
          dispatch(resetComposer())
        })
        .catch(() => {
          dispatch(message(intl.formatMessage({ id: 'errors.generic' })))
        })
        .finally(() => {
          setIsDeleting(false)
        })
    }
  }

  const shouldConfirmSwitchToDefault = !isDefaultView && (multiPostsCount > 0 || Boolean(bulkFile))
  const hideClearBtn = isComposerEmpty && multiPostsCount === 0 && !bulkFile

  return (
    <div className={styles.header} data-testid="composer-header">
      <div>
        <Logo className={styles.logo} />
        <ConfirmDialog
          message={<FormattedMessage id="composer.notifications.clear-bulk-content" />}
          variant="danger"
          confirmLabel={<FormattedMessage id="actions.clear" />}
        >
          {(confirm) => (
            <h3 className={styles.title}>
              <FormattedMessage
                id={isEditingPost ? 'composer.labels.editing-posts-count' : 'composer.labels.creating-posts-count'}
                values={{
                  posts: multiPostsCount === 0 ? selectedProfilesCount : multiPostsCount,
                  profiles: selectedProfilesCount,
                  select: (_text) => (
                    <PPSelect
                      options={viewOptions}
                      selectedValue={props.activeView}
                      withCaret
                      buttonClassName={styles['header-btn']}
                      className={styles.select}
                      noLabelCaps
                      onSelectionChange={shouldConfirmSwitchToDefault ? confirm(props.onActiveViewChange) : props.onActiveViewChange}
                    />
                  )
                }}
              />
            </h3>
          )}
        </ConfirmDialog>
      </div>
      <div className={styles.right}>
        {isEditingPost && (
          <ConfirmDialog
            message={<FormattedMessage id="post.delete-confirm" />}
            variant="danger"
            confirmLabel={<FormattedMessage id="actions.delete" />}
          >
            {(confirm) => (
              <Button
                size="small"
                className={styles.btn}
                disabled={isDeleting}
                onClick={confirm(onDelete)}
              >
                <FormattedMessage id="actions.delete" />
              </Button>
            )}
          </ConfirmDialog>
        )}
        {!isEditingPost && !hideClearBtn && (
          <ConfirmDialog
            message={<FormattedMessage id="composer.labels.confirm-clear" />}
            variant="danger"
            confirmLabel={<FormattedMessage id="actions.clear" />}
          >
            {(confirm) => (
              <Button
                size="small"
                className={styles.btn}
                onClick={confirm(onClear)}
              >
                <FormattedMessage id="actions.clear" />
              </Button>
            )}
          </ConfirmDialog>
        )}
        <Button size="small" className={styles.btn} onClick={close}>
          <FormattedMessage id="actions.close" />
        </Button>
        {calloutIsHidden && (
          <IconButton size="small" color="primary" className={`${styles.btn} ${styles['btn-help']}`} onClick={showInfobox}>
            <Icon size="20px" path={mdiHelp} />
          </IconButton>
        )}
      </div>
    </div>
  )
}

export default Header
